// React and core dependencies
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Redirect, useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { t } from 'ttag';
import * as queryString from 'query-string';

// Import unified components
import {
  AuthLayout,
  AuthForm,
  AuthField,
  AuthButton,
  AuthLink,
  AuthMessage
} from '../UnifiedComponents';

// Import existing utilities and components
import { LoginSchema } from './login.utils';
import { useToast } from '@coolremote/shared';
import TwoFactorAuthDialog from '../TwoFactorAuthDialog/TwoFactorAuthDialog';
import ReportProblem from './../../screens/ReportProblem/ReportProblem';
import { useStoreActions, useStoreState } from '@models/RootStore';
import { clearPersistedSelection } from '@models/Selections';
import assetsService from '@services/assetsService';
import SupportTicket from '@icons/SupportTicket';
import styles from './UnifiedLogin.module.css';

// Constants
const THEME_VARIANTS = {
  LIGHT: 'light',
  DARK: 'dark',
};

// Red-orange used by the "Register Now" link in the CM-168 design
const REGISTER_LINK_COLOR = '#E23C10';

const INITIAL_LOGIN_STATE = {
  username: '',
  password: '',
};

const ERROR_MESSAGES = {
  INVALID_2FA_CODE: t`The code you entered is invalid or expired. Please check again you entered the right code, or try logging in again to receive a new code.`,
};

/**
 * Unified Login Component
 *
 * A comprehensive login component that supports both mobile and desktop layouts
 * with light and dark theme variants. Includes integrated form validation,
 * 2FA support, error handling, and support ticket functionality.
 *
 * Features:
 * - Responsive design (mobile/desktop)
 * - Theme support (light/dark)
 * - Form validation with Formik
 * - Two-factor authentication
 * - Error handling and display
 * - Support ticket integration
 * - Forgot password functionality
 *
 * @component
 * @param {Object} props - Component props
 * @param {('light'|'dark')} [props.theme='dark'] - Theme variant
 * @param {boolean} [props.isMobile=false] - Renders the mobile layout, including the
 *   "Register Now" entry point to the device registration flow
 * @returns {JSX.Element} The rendered login component
 */
const UnifiedLogin = ({ theme = THEME_VARIANTS.DARK, isMobile = false }) => {
  const location = useLocation();
  const history = useHistory();

  // A stale header selection from a previous session must not leak into a new login.
  useEffect(() => {
    clearPersistedSelection();
  }, []);

  // Store state and actions
  const { configFile, isLoggedIn } = useStoreState((state) => ({
    configFile: state.config || {},
    isLoggedIn: state.isLoggedIn,
  }));
  const { doLogin, verify2FA } = useStoreActions((actions) => ({
    doLogin: actions.doLogin,
    verify2FA: actions.verify2FA,
  }));

  // Component state management
  const [dialogStates, setDialogStates] = useState({
    twoFA: false,
    reportProblem: false,
  });

  const { flashError } = useToast();

  const [errorStates, setErrorStates] = useState({
    twoFA: null,
  });

  const [loginFormState, setLoginFormState] = useState(INITIAL_LOGIN_STATE);

  // Login button state management
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  // Destructure config file properties
  const { urls, enableOpenTicket, supportImg } = configFile || {};

  // Memoized logo source
  const logoSrc = useMemo(() => assetsService.getResourcePath('logo.png'), []);

  /**
   * Handles user login submission
   * @param {Object} credentials - User login credentials
   * @param {string} credentials.username - Username
   * @param {string} credentials.password - Password
   */
  const handleUserLogin = useCallback(async (credentials) => {
    // Set loading state
    setIsLoggingIn(true);

    // Clear previous errors
    setLoginFormState(prev => ({ ...prev, error: null }));

    try {
      const loginResponse = await doLogin({
        username: credentials.username,
        password: credentials.password,
      });

      // Check if 2FA is required
      if (loginResponse?.is2FA) {
        setDialogStates(prev => ({ ...prev, twoFA: true }));
      }
    } catch (error) {
      // Handle permission errors for specific environments
      if (error?.hasNoPermission && window.location.href.includes('airconnectpro')) {
        // Redirect to control app
        const env = window.location.origin.indexOf('-dev') !== -1 || window.location.origin.indexOf('localhost') !== -1 ? 'dev' :
                   window.location.origin.indexOf('-stg') !== -1 ? 'stg' : 'prod';
        const controlUrl = urls?.[env];
        if (controlUrl) {
          window.location.href = controlUrl;
        }
        return;
      }

      // Set login error message
      if (error?.message) {
        setLoginFormState(prev => ({ ...prev, error: error.message }));
      }
    } finally {
      // Reset loading state
      setIsLoggingIn(false);
    }
  }, [doLogin, urls]);

  /**
   * Handles 2FA code verification
   * @param {string} verificationCode - The 2FA verification code
   */
  const handleTwoFactorVerification = useCallback(async (verificationCode) => {
    // Clear previous 2FA errors
    setErrorStates(prev => ({ ...prev, twoFA: null }));

    try {
      await verify2FA({ code: verificationCode });
      // Success - dialog will close automatically
    } catch (error) {
      // Handle permission errors
      if (error?.hasNoPermission && window.location.href.includes('airconnectpro')) {
        const env = window.location.origin.indexOf('-dev') !== -1 || window.location.origin.indexOf('localhost') !== -1 ? 'dev' :
                   window.location.origin.indexOf('-stg') !== -1 ? 'stg' : 'prod';
        const controlUrl = urls?.[env];
        if (controlUrl) {
          window.location.href = controlUrl;
        }
        return;
      }

      // Set 2FA error message
      if (error?.message) {
        setErrorStates(prev => ({
          ...prev,
          twoFA: ERROR_MESSAGES.INVALID_2FA_CODE
        }));
      }
    }
  }, [verify2FA, urls]);

  /**
   * Handles forgot password navigation
   */
  const handleForgotPasswordNavigation = useCallback(() => {
    history.push('/forgot-password');
  }, [history]);

  /**
   * Handles navigation to the public device registration flow
   */
  const handleRegisterDeviceNavigation = useCallback(() => {
    history.push('/register-device');
  }, [history]);

  /**
   * Handles support ticket button click
   */
  const handleSupportTicketRequest = useCallback(() => {
    // If external support link is configured, open it
    if (configFile?.supportBtnLink?.length) {
      window?.open(configFile.supportBtnLink, '_blank')?.focus();
      return;
    }

    // Otherwise, open internal report dialog
    setDialogStates(prev => ({ ...prev, reportProblem: true }));
  }, [configFile?.supportBtnLink]);

  /**
   * Closes the 2FA dialog and resets related state
   */
  const closeTwoFactorDialog = useCallback(() => {
    setDialogStates(prev => ({ ...prev, twoFA: false }));
    setErrorStates(prev => ({ ...prev, twoFA: null }));
  }, []);

  /**
   * Closes the report problem dialog
   */
  const closeReportProblemDialog = useCallback(() => {
    setDialogStates(prev => ({ ...prev, reportProblem: false }));
  }, []);

  /**
   * Resets 2FA verification error
   */
  const resetTwoFactorError = useCallback(() => {
    setErrorStates(prev => ({ ...prev, twoFA: null }));
  }, []);

  /**
   * Renders the links under the login form.
   *
   * On mobile the "Register Now" entry point to the device registration flow sits
   * next to "Forgot Password?" on a single row. On desktop only the forgot password
   * link is shown, unchanged.
   */
  const renderAuthLinks = () => {
    const forgotPasswordLink = (
      <AuthLink
        theme={theme}
        onClick={handleForgotPasswordNavigation}
        ariaLabel={t`Navigate to forgot password page`}
      >
        {t`Forgot Password?`}
      </AuthLink>
    );

    if (!isMobile) {
      return (
        <div className={styles.authLinksCentered}>
          {forgotPasswordLink}
        </div>
      );
    }

    return (
      <div className={styles.authLinksRow}>
        <AuthLink
          theme={theme}
          onClick={handleRegisterDeviceNavigation}
          ariaLabel={t`Register a new device`}
          color={REGISTER_LINK_COLOR}
        >
          {t`Register Now`}
        </AuthLink>
        {forgotPasswordLink}
      </div>
    );
  };

  // Redirect if user is already authenticated
  if (isLoggedIn) {
    const urlParams = queryString.parse(location.search);
    const redirectPath = urlParams.redirect || '/dashboard';

    return (
      <div style={{ textAlign: 'center', padding: '20px' }}>
        <p>{t`User already logged in, redirecting...`}</p>
        <Redirect to={redirectPath} />
      </div>
    );
  }

  return (
    <>
      <AuthLayout
        theme={theme}
        title={t`Welcome! Please login`}
        subtitle={t`Please sign in to your account`}
        logo={logoSrc}
        onLogoError={() => {
          // Handle logo error silently
        }}
      >
        {/* Login Form */}
        <AuthForm
          theme={theme}
          initialValues={loginFormState}
          validationSchema={LoginSchema}
          onSubmit={handleUserLogin}
          submitButtonText={isLoggingIn ? t`Logging in...` : t`LOGIN`}
          isSubmitting={isLoggingIn}
        >
          {/* Username Field */}
          <AuthField
            theme={theme}
            label={t`Username`}
            name="username"
            required
          />

          {/* Password Field */}
          <AuthField
            theme={theme}
            label={t`Password`}
            name="password"
            type="password"
            required
          />

          {/* Login Error Display */}
          {loginFormState.error && (
            <AuthMessage
              theme={theme}
              type="error"
              message={loginFormState.error}
            />
          )}
        </AuthForm>

        {/* Register Now / Forgot Password links */}
        {renderAuthLinks()}

        {/* Support Ticket Button */}
        {enableOpenTicket && (
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <AuthButton
              theme={theme}
              variant="secondary"
              onClick={handleSupportTicketRequest}
            >
              {supportImg ? (
                <img
                  src={assetsService.getResourcePath(supportImg)}
                  alt={t`Support`}
                  style={{ width: '20px', height: '20px', marginRight: '8px' }}
                />
              ) : (
                <SupportTicket width="20px" height="20px" style={{ marginRight: '8px' }} />
              )}
              {t`Open Support Ticket`}
            </AuthButton>
          </div>
        )}
      </AuthLayout>

      {/* Support Ticket Dialog */}
      {dialogStates.reportProblem && (
        <ReportProblem
          openReport
          location="login"
          onClose={closeReportProblemDialog}
        />
      )}

      {/* Two-Factor Authentication Dialog */}
      <TwoFactorAuthDialog
        onCancel={closeTwoFactorDialog}
        onConfirm={handleTwoFactorVerification}
        onClose={closeTwoFactorDialog}
        title={t`Additional authentication required`}
        text={t`Your account requires an additional authentication code that was sent to your email address. Please enter the code below`}
        confirmLabel={t`Submit`}
        cancelLabel={t`Cancel`}
        open={dialogStates.twoFA}
        errorText={errorStates.twoFA}
        onInputChange={resetTwoFactorError}
        align={"left"}
      />
    </>
  );
};

// Prop types and default props
UnifiedLogin.propTypes = {
  /** Theme variant for the login component */
  theme: PropTypes.oneOf([THEME_VARIANTS.LIGHT, THEME_VARIANTS.DARK]),
  /** Renders the mobile layout, including the "Register Now" link */
  isMobile: PropTypes.bool,
};

UnifiedLogin.defaultProps = {
  theme: THEME_VARIANTS.DARK,
  isMobile: false,
};

export default UnifiedLogin;
